import React, { useState, useEffect } from 'react';
import { Ship, Plus, Edit2, Trash2, Upload, Camera, Anchor, Flag, Calendar, Gauge, X, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { uploadToCloudinary } from '../utils/cloudinaryUpload';
import Header from '../components/Header';

const API = process.env.REACT_APP_BACKEND_URL;

const vesselTypes = [
  { value: 'container', label: 'Container Ship' },
  { value: 'bulk_carrier', label: 'Bulk Carrier' },
  { value: 'tanker', label: 'Tanker' },
  { value: 'roro', label: 'RoRo' },
  { value: 'tug', label: 'Tug' },
  { value: 'barge', label: 'Barge' },
  { value: 'general_cargo', label: 'General Cargo' },
  { value: 'reefer', label: 'Reefer' },
  { value: 'chemical_tanker', label: 'Chemical Tanker' },
  { value: 'oil_tanker', label: 'Oil Tanker' },
  { value: 'lpg_carrier', label: 'LPG Carrier' },
  { value: 'lng_carrier', label: 'LNG Carrier' },
  { value: 'car_carrier', label: 'Car Carrier' },
  { value: 'heavy_lift', label: 'Heavy Lift' },
  { value: 'offshore_supply', label: 'Offshore Supply' },
  { value: 'multipurpose', label: 'Multipurpose' },
  { value: 'passenger_ferry', label: 'Passenger/Cargo Ferry' },
  { value: 'other', label: 'Other' }
];

const availabilityStatuses = [
  { value: 'available', label: 'Available', color: 'bg-green-100 text-green-800' },
  { value: 'chartered', label: 'Currently Chartered', color: 'bg-blue-100 text-blue-800' },
  { value: 'in_maintenance', label: 'In Maintenance', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'drydock', label: 'In Drydock', color: 'bg-orange-100 text-orange-800' }
];

const FleetManagement = () => {
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingVessel, setEditingVessel] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');

  const emptyVessel = {
    name: '',
    imo_number: '',
    mmsi: '',
    call_sign: '',
    vessel_type: '',
    flag_state: '',
    dwt: '',
    grt: '',
    loa: '',
    beam: '',
    draft: '',
    teu_capacity: '',
    main_engine: '',
    speed_knots: '',
    year_built: '',
    builder: '',
    class_society: '',
    photos: [],
    current_location: '',
    availability_status: 'available',
    special_equipment: [],
    certifications: [],
    notes: ''
  };

  const [formData, setFormData] = useState(emptyVessel);

  useEffect(() => {
    fetchVessels();
  }, []);

  const fetchVessels = async () => {
    try {
      const res = await fetch(`${API}/api/vessels/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVessels(data);
      }
    } catch (error) {
      console.error('Error fetching vessels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(file => uploadToCloudinary(file, token));
      const urls = await Promise.all(uploadPromises);
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...urls.filter(u => u)]
      }));
      toast.success(`${urls.length} photo(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.imo_number || !formData.vessel_type || !formData.flag_state) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        dwt: formData.dwt ? parseFloat(formData.dwt) : null,
        grt: formData.grt ? parseFloat(formData.grt) : null,
        loa: formData.loa ? parseFloat(formData.loa) : null,
        beam: formData.beam ? parseFloat(formData.beam) : null,
        draft: formData.draft ? parseFloat(formData.draft) : null,
        teu_capacity: formData.teu_capacity ? parseInt(formData.teu_capacity) : null,
        speed_knots: formData.speed_knots ? parseFloat(formData.speed_knots) : null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null
      };

      const url = editingVessel 
        ? `${API}/api/vessels/${editingVessel.id}`
        : `${API}/api/vessels`;
      
      const method = editingVessel ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (res.ok) {
        toast.success(editingVessel ? 'Vessel updated!' : 'Vessel added to fleet!');
        setShowAddDialog(false);
        setEditingVessel(null);
        setFormData(emptyVessel);
        fetchVessels();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save vessel');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vesselId) => {
    if (!window.confirm('Are you sure you want to remove this vessel from your fleet?')) return;

    try {
      const res = await fetch(`${API}/api/vessels/${vesselId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Vessel removed');
        fetchVessels();
      }
    } catch (error) {
      toast.error('Failed to remove vessel');
    }
  };

  const openEditDialog = (vessel) => {
    setEditingVessel(vessel);
    setFormData({
      ...vessel,
      dwt: vessel.dwt?.toString() || '',
      grt: vessel.grt?.toString() || '',
      loa: vessel.loa?.toString() || '',
      beam: vessel.beam?.toString() || '',
      draft: vessel.draft?.toString() || '',
      teu_capacity: vessel.teu_capacity?.toString() || '',
      speed_knots: vessel.speed_knots?.toString() || '',
      year_built: vessel.year_built?.toString() || ''
    });
    setShowAddDialog(true);
  };

  const getVesselTypeLabel = (type) => {
    const found = vesselTypes.find(v => v.value === type);
    return found ? found.label : type;
  };

  const getStatusBadge = (status) => {
    const found = availabilityStatuses.find(s => s.value === status);
    return found ? (
      <Badge className={found.color}>{found.label}</Badge>
    ) : (
      <Badge>{status}</Badge>
    );
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-7xl mx-auto px-4">
          {/* Page Header */}
          <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Fleet Management</h1>
            <p className="text-slate-600 mt-1">Manage your company's vessels and availability</p>
          </div>
          <Button
            className="bg-cyan-500 hover:bg-cyan-600"
            onClick={() => {
              setEditingVessel(null);
              setFormData(emptyVessel);
              setShowAddDialog(true);
            }}
            data-testid="add-vessel-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Vessel
          </Button>
        </div>

        {/* Fleet Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <Ship className="h-8 w-8 mx-auto text-cyan-500 mb-2" />
              <div className="text-2xl font-bold">{vessels.length}</div>
              <div className="text-sm text-slate-500">Total Vessels</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Check className="h-8 w-8 mx-auto text-green-500 mb-2" />
              <div className="text-2xl font-bold">
                {vessels.filter(v => v.availability_status === 'available').length}
              </div>
              <div className="text-sm text-slate-500">Available</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Anchor className="h-8 w-8 mx-auto text-blue-500 mb-2" />
              <div className="text-2xl font-bold">
                {vessels.filter(v => v.availability_status === 'chartered').length}
              </div>
              <div className="text-sm text-slate-500">Chartered</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Gauge className="h-8 w-8 mx-auto text-orange-500 mb-2" />
              <div className="text-2xl font-bold">
                {vessels.reduce((sum, v) => sum + (v.dwt || 0), 0).toLocaleString()}
              </div>
              <div className="text-sm text-slate-500">Total DWT</div>
            </CardContent>
          </Card>
        </div>

        {/* Vessels List */}
        {loading ? (
          <div className="text-center py-12">Loading fleet...</div>
        ) : vessels.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Ship className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Vessels in Fleet</h3>
              <p className="text-slate-500 mb-4">Add your first vessel to start receiving charter inquiries</p>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Vessel
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {vessels.map(vessel => (
              <Card key={vessel.id} className="hover:shadow-lg transition-shadow" data-testid={`vessel-${vessel.id}`}>
                <CardContent className="p-6">
                  <div className="flex gap-6">
                    {/* Vessel Photo */}
                    <div className="w-48 h-32 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                      {vessel.photos?.[0] ? (
                        <img 
                          src={vessel.photos[0]} 
                          alt={vessel.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Ship className="h-12 w-12 text-slate-300" />
                        </div>
                      )}
                    </div>

                    {/* Vessel Info */}
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="text-xl font-bold">{vessel.name}</h3>
                          <p className="text-sm text-slate-500">IMO: {vessel.imo_number}</p>
                        </div>
                        {getStatusBadge(vessel.availability_status)}
                      </div>

                      <div className="grid grid-cols-4 gap-4 text-sm mb-4">
                        <div>
                          <span className="text-slate-500">Type:</span>
                          <p className="font-medium">{getVesselTypeLabel(vessel.vessel_type)}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Flag:</span>
                          <p className="font-medium">{vessel.flag_state}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">DWT:</span>
                          <p className="font-medium">{vessel.dwt?.toLocaleString() || '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Built:</span>
                          <p className="font-medium">{vessel.year_built || '-'}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">LOA:</span>
                          <p className="font-medium">{vessel.loa ? `${vessel.loa}m` : '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Beam:</span>
                          <p className="font-medium">{vessel.beam ? `${vessel.beam}m` : '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Draft:</span>
                          <p className="font-medium">{vessel.draft ? `${vessel.draft}m` : '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Speed:</span>
                          <p className="font-medium">{vessel.speed_knots ? `${vessel.speed_knots} kn` : '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(vessel)}
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(vessel.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Vessel Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Ship className="h-5 w-5 text-cyan-500" />
                {editingVessel ? 'Edit Vessel' : 'Add New Vessel'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-semibold border-b pb-2">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Vessel Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="M/V Example"
                      required
                      data-testid="vessel-name-input"
                    />
                  </div>
                  <div>
                    <Label>IMO Number *</Label>
                    <Input
                      value={formData.imo_number}
                      onChange={(e) => handleChange('imo_number', e.target.value)}
                      placeholder="1234567"
                      required
                      data-testid="vessel-imo-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>MMSI</Label>
                    <Input
                      value={formData.mmsi}
                      onChange={(e) => handleChange('mmsi', e.target.value)}
                      placeholder="123456789"
                    />
                  </div>
                  <div>
                    <Label>Call Sign</Label>
                    <Input
                      value={formData.call_sign}
                      onChange={(e) => handleChange('call_sign', e.target.value)}
                      placeholder="ABCD"
                    />
                  </div>
                  <div>
                    <Label>Flag State *</Label>
                    <Input
                      value={formData.flag_state}
                      onChange={(e) => handleChange('flag_state', e.target.value)}
                      placeholder="e.g., Panama"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Vessel Type *</Label>
                    <Select
                      value={formData.vessel_type}
                      onValueChange={(v) => handleChange('vessel_type', v)}
                    >
                      <SelectTrigger data-testid="vessel-type-input">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {vesselTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Availability Status</Label>
                    <Select
                      value={formData.availability_status}
                      onValueChange={(v) => handleChange('availability_status', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availabilityStatuses.map(status => (
                          <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Specifications */}
              <div className="space-y-4">
                <h4 className="font-semibold border-b pb-2">Specifications</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>DWT (tons)</Label>
                    <Input
                      type="number"
                      value={formData.dwt}
                      onChange={(e) => handleChange('dwt', e.target.value)}
                      placeholder="50000"
                    />
                  </div>
                  <div>
                    <Label>GRT (tons)</Label>
                    <Input
                      type="number"
                      value={formData.grt}
                      onChange={(e) => handleChange('grt', e.target.value)}
                      placeholder="30000"
                    />
                  </div>
                  <div>
                    <Label>LOA (meters)</Label>
                    <Input
                      type="number"
                      value={formData.loa}
                      onChange={(e) => handleChange('loa', e.target.value)}
                      placeholder="180"
                    />
                  </div>
                  <div>
                    <Label>Beam (meters)</Label>
                    <Input
                      type="number"
                      value={formData.beam}
                      onChange={(e) => handleChange('beam', e.target.value)}
                      placeholder="30"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>Draft (meters)</Label>
                    <Input
                      type="number"
                      value={formData.draft}
                      onChange={(e) => handleChange('draft', e.target.value)}
                      placeholder="12"
                    />
                  </div>
                  <div>
                    <Label>TEU Capacity</Label>
                    <Input
                      type="number"
                      value={formData.teu_capacity}
                      onChange={(e) => handleChange('teu_capacity', e.target.value)}
                      placeholder="4000"
                    />
                  </div>
                  <div>
                    <Label>Speed (knots)</Label>
                    <Input
                      type="number"
                      value={formData.speed_knots}
                      onChange={(e) => handleChange('speed_knots', e.target.value)}
                      placeholder="14"
                    />
                  </div>
                  <div>
                    <Label>Year Built</Label>
                    <Input
                      type="number"
                      value={formData.year_built}
                      onChange={(e) => handleChange('year_built', e.target.value)}
                      placeholder="2015"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Builder/Shipyard</Label>
                    <Input
                      value={formData.builder}
                      onChange={(e) => handleChange('builder', e.target.value)}
                      placeholder="e.g., Hyundai Heavy Industries"
                    />
                  </div>
                  <div>
                    <Label>Class Society</Label>
                    <Input
                      value={formData.class_society}
                      onChange={(e) => handleChange('class_society', e.target.value)}
                      placeholder="e.g., Lloyd's, DNV, ABS"
                    />
                  </div>
                </div>
              </div>

              {/* Photos */}
              <div className="space-y-4">
                <h4 className="font-semibold border-b pb-2">Vessel Photos</h4>
                <div className="grid grid-cols-4 gap-4">
                  {formData.photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={photo}
                        alt={`Vessel ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <label className="border-2 border-dashed border-slate-300 rounded-lg h-24 flex flex-col items-center justify-center cursor-pointer hover:border-cyan-500 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                    {uploading ? (
                      <span className="text-sm text-slate-500">Uploading...</span>
                    ) : (
                      <>
                        <Camera className="h-6 w-6 text-slate-400 mb-1" />
                        <span className="text-xs text-slate-500">Add Photo</span>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Additional information about the vessel..."
                  rows={3}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-4 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddDialog(false);
                    setEditingVessel(null);
                    setFormData(emptyVessel);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-cyan-500 hover:bg-cyan-600"
                  disabled={saving}
                  data-testid="save-vessel-btn"
                >
                  {saving ? 'Saving...' : (editingVessel ? 'Update Vessel' : 'Add Vessel')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
    </>
  );
};

export default FleetManagement;
